import { useEffect, useState } from 'react';

// Types
import { MapboxData } from '@shared/types';

// Configs
import ENV from '@shared/configs';

// Actions
import { getAddressSuggestions } from '@/actions/client/mapbox';

// Utils
import {
  isSearchTermValid,
  normalizeAddressData,
  normalizeSearchValue,
} from '@shared/utils';
import { showErrorToast } from '@/utils/errorHandlers';

// Providers
import { useFirebase } from '@/providers/firebase';

const useAddressAutocomplete = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<MapboxData[]>(
    [],
  );
  const { getToken } = useFirebase();
  const [isLoading, setIsLoading] = useState(false);

  // Fetch address suggestions from API
  useEffect(() => {
    if (!isUserTyping || searchTerm.trim() === '') return;
    if (searchTerm.endsWith(' ')) return;

    const getAddressSuggestionList = async () => {
      const token = await getToken();

      if (
        !isSearchTermValid(searchTerm, lastSearchTerm) ||
        !ENV.MAPBOX_ACCESS_TOKEN
      ) {
        return;
      }

      if (searchTerm.trim().length < ENV.MAPBOX_MIN_SEARCH_LENGTH) {
        return;
      }

      setIsLoading(true);

      try {
        const { data, error } = await getAddressSuggestions(searchTerm, {
          token,
        });

        if (error) {
          showErrorToast(error);
        }

        // Normalize Vietnamese characters to English in the data
        const normalizedData = data
          ? data.map((item: MapboxData) => normalizeAddressData(item))
          : [];

        setAddressSuggestions(normalizedData);
        setLastSearchTerm(searchTerm);
      } catch (error) {
        showErrorToast(error);
        setAddressSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    getAddressSuggestionList();

    // Don't need to put "getToken" in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, lastSearchTerm, isUserTyping]);

  // Handle search term change
  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(normalizeSearchValue(newSearchTerm));
    setIsUserTyping(true);
  };

  // Reset all states
  const resetSearch = () => {
    setSearchTerm('');
    setLastSearchTerm('');
    setIsUserTyping(false);
    setAddressSuggestions([]);
  };

  return {
    addressSuggestions,
    isLoading,
    searchTerm,
    isUserTyping,
    handleSearchChange,
    resetSearch,
    setIsUserTyping,
  };
};

export default useAddressAutocomplete;
